import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { itemCategoryApiService, ItemCategoryCreateRequest } from '../services/itemCategoryApi';

interface CreateCategoryDialogProps {
  onCategoryCreated: () => void;
}

export function CreateCategoryDialog({ onCategoryCreated }: CreateCategoryDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ItemCategoryCreateRequest>({
    categoryName: '',
    description: '',
    isActive: true,
  });

  const handleInputChange = (field: keyof ItemCategoryCreateRequest, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    setLoading(true);
    try {
      await itemCategoryApiService.createItemCategory(formData);
      setFormData({
        categoryName: '',
        description: '',
        isActive: true,
      });
      setOpen(false);
      onCategoryCreated();
    } catch (error) {
      // console.error('Error creating item category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      categoryName: '',
      description: '',
      isActive: true,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2 border-primary-200 text-primary-600 hover:bg-primary-50"
        >
          <Plus className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Item Category</DialogTitle>
          <DialogDescription>
            Add a new item category for raw items. Fill in the details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="categoryName">Category Name *</Label>
            <Input
              id="categoryName"
              value={formData.categoryName}
              onChange={(e) => handleInputChange('categoryName', e.target.value)}
              placeholder="Enter category name"
              className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              disabled={loading}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter category description"
              className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              disabled={loading}
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => handleInputChange('isActive', checked)}
              disabled={loading}
            />
            <Label htmlFor="isActive" className="text-sm">
              Active
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-primary-200 text-primary-600 hover:bg-primary-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            >
              {loading ? 'Creating...' : 'Create Item Category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
